// Query options for the reads several pages share. Pages call useQuery(referenceQuery) etc.
// Keys: ['reference'], ['workflow', ...], ['apps'], ['app', id], ['assignable', appId], ['people', q].
import { WORKFLOW_CODES, type WorkflowCode } from '@feedback/shared/constants/workflow';
import type { AppSummary, AppView } from '@feedback/shared/contracts/apps';
import type { PrincipalSummary } from '@feedback/shared/contracts/identity';
import type { ReferenceData } from '@feedback/shared/contracts/settings';
import type { VersionSummary, VersionView, WorkflowStage, WorkflowStatus } from '@feedback/shared/contracts/workflow';
import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { api, seg } from './api';

/** Reference lists change rarely (S12 Lists). */
const REFERENCE_STALE_MS = 5 * 60_000;

export const referenceQuery = queryOptions({
  queryKey: ['reference'],
  queryFn: () => api.get<ReferenceData>('/api/reference'),
  staleTime: REFERENCE_STALE_MS,
});

/** One workflow's published versions (`?workflow=`, default bug; poc CT-004). */
export const workflowVersionsQuery = (workflow: WorkflowCode = WORKFLOW_CODES.bug) =>
  queryOptions({
    queryKey: ['workflow', 'versions', workflow],
    queryFn: async () => (await api.get<{ items: VersionSummary[] }>('/api/workflow/versions', { workflow })).items,
    staleTime: REFERENCE_STALE_MS,
  });

export const workflowVersionQuery = (id: string) =>
  queryOptions({
    queryKey: ['workflow', 'version', id],
    queryFn: () => api.get<VersionView>(`/api/workflow/versions/${seg(id)}`),
    staleTime: REFERENCE_STALE_MS,
  });

export const appsQuery = queryOptions({
  queryKey: ['apps'],
  queryFn: async () => (await api.get<{ items: AppSummary[] }>('/api/apps')).items,
});

export const appQuery = (appId: string) =>
  queryOptions({
    queryKey: ['app', appId],
    queryFn: () => api.get<AppView>(`/api/apps/${seg(appId)}`),
  });

/**
 * The people tickets can be assigned to (REQ-8.5, I-7): every active Editor and Admin, the same list for every
 * app (poc CT-008); the route names an app and needs ticket.assign there.
 */
export const assignableQuery = (appId: string) =>
  queryOptions({
    queryKey: ['assignable', appId],
    queryFn: () => api.get<PrincipalSummary[]>(`/api/apps/${seg(appId)}/assignable`),
  });

/** Editors and Admins whose name or UPN matches `q`, at most 20 (tool-wide, poc CT-008). */
export const peopleQuery = (q: string) =>
  queryOptions({
    queryKey: ['people', q],
    queryFn: () => api.get<PrincipalSummary[]>('/api/people', { q }),
  });

/** A status from any workflow version, with the names it has had (REQ-8.2: "status from any version"). */
export interface AnyVersionStatus {
  id: string;
  stageCode: WorkflowStatus['stageCode'];
  /** The name in the newest version that has it. */
  name: string;
  /** "Needs review (v1, v2)" */
  label: string;
}

/**
 * Every status of every published version of one workflow (default bug), newest names first; and its
 * stages (fixed) by their newest names.
 */
export function useAllStatuses(workflow: WorkflowCode = WORKFLOW_CODES.bug): {
  statuses: AnyVersionStatus[];
  stages: WorkflowStage[];
  isPending: boolean;
} {
  const versions = useQuery(workflowVersionsQuery(workflow));
  const details = useQueries({
    queries: (versions.data ?? []).map((v) => workflowVersionQuery(v.id)),
  });
  const loaded = details.map((d) => d.data).filter((d): d is VersionView => d !== undefined);
  const byId = new Map<string, { status: WorkflowStatus; versions: number[] }>();
  const newestFirst = [...loaded].sort((a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0));
  for (const version of newestFirst) {
    for (const status of version.statuses) {
      const entry = byId.get(status.id);
      if (entry) {
        entry.versions.push(version.versionNumber ?? 0);
      } else {
        byId.set(status.id, { status, versions: [version.versionNumber ?? 0] });
      }
    }
  }
  const statuses = [...byId.values()].map(({ status, versions: nums }) => ({
    id: status.id,
    stageCode: status.stageCode,
    name: status.name,
    label: `${status.name} (${nums
      .sort((a, b) => a - b)
      .map((n) => `v${n}`)
      .join(', ')})`,
  }));
  const stages = [...(newestFirst[0]?.stages ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  return { statuses, stages, isPending: versions.isPending || details.some((d) => d.isPending) };
}

/**
 * The real name of a workflow's Review default ("Needs review", "In review" where a version renamed it, a
 * feature request's "Backlog"): where a type change puts a ticket (module-3 6.2, poc CT-005). In the current
 * published version, or in the version `versionNumber` when given. Null while it loads, or when not `enabled`.
 */
export function useReviewDefaultName(
  workflow: WorkflowCode,
  { enabled = true, versionNumber = null }: { enabled?: boolean; versionNumber?: number | null } = {},
): string | null {
  const versions = useQuery({ ...workflowVersionsQuery(workflow), enabled });
  const id = versions.data?.find((v) => (versionNumber === null ? v.isCurrent : v.versionNumber === versionNumber))?.id;
  const version = useQuery({ ...workflowVersionQuery(id ?? ''), enabled: enabled && id !== undefined });
  const review = version.data?.stages.find((s) => s.code === 'review');
  return version.data?.statuses.find((s) => s.id === review?.defaultStatusId)?.name ?? null;
}

export interface StatusGroup {
  workflow: WorkflowCode;
  /** "Bug" or "Feature request": the Status filter's group heading. */
  label: string;
  statuses: AnyVersionStatus[];
}

/**
 * The Status filter's options: both workflows' statuses, grouped by type (poc CT-004, module-4 Section 4).
 * Status IDs are distinct across workflows, so the `status` filter takes either group's IDs unchanged.
 */
export function useStatusGroups(): StatusGroup[] {
  const bug = useAllStatuses(WORKFLOW_CODES.bug);
  const featureRequest = useAllStatuses(WORKFLOW_CODES.featureRequest);
  return [
    { workflow: WORKFLOW_CODES.bug, label: 'Bug', statuses: bug.statuses },
    { workflow: WORKFLOW_CODES.featureRequest, label: 'Feature request', statuses: featureRequest.statuses },
  ];
}
